import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UserAuth } from './AuthContext.jsx';
import { getMyGameAccessRequests } from '../services/admin-settings/UserGameAccessService.jsx';
import { LANDING_PATH } from '../config/accessControl.js';

// Shared with the "My Access Requests" banner so acknowledging an approval in one place is
// remembered everywhere — the prompt never nags for grants the user already acted on.
const ACK_STORAGE_KEY = 'gp_game_access_acked';

/** Stable identity for a request row (so we can remember which approvals the user already acted on). */
const requestKey = (row) =>
    row?.requestId || row?.id || `${row?.gameClientId || ''}:${row?.role || ''}`;

const readAcked = () => {
    try {
        return new Set(JSON.parse(localStorage.getItem(ACK_STORAGE_KEY) || '[]'));
    } catch (_) {
        return new Set();
    }
};

const writeAcked = (set) => {
    try {
        localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(Array.from(set)));
    } catch (_) { /* ignore */ }
};

/**
 * Detects the calling user's game-access requests that have been APPROVED but not yet acknowledged,
 * and exposes a one-click "refresh access" action that re-hydrates profile + effective permissions
 * (no re-login) and lands on the home dashboard — the same flow the org-join waiting screen
 * ({@code DemoPage}) and the "My Access Requests" banner use.
 *
 * <p>Re-checks on mount AND when the user returns to the tab (focus / visibility), so an approval
 * that lands while they're away (acting on the email / notification) is picked up the moment they
 * come back — no timer. Acknowledged approvals are remembered in localStorage (a key shared with the
 * My-Access-Requests banner) so the prompt doesn't reappear for old grants.</p>
 *
 * @returns {{ requests: Array, loading: boolean, newlyApproved: Array, entering: boolean,
 *             onEnter: () => Promise<void>, refresh: () => Promise<void> }}
 */
export const useApprovedGameAccessRefresh = () => {
    const { user } = UserAuth();

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acked, setAcked] = useState(() => readAcked());
    const [entering, setEntering] = useState(false);
    const checkingRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!user?.accessToken || checkingRef.current) return;
        checkingRef.current = true;
        setLoading(true);
        try {
            // Surface every APPROVED-but-unacknowledged request as a "refresh to load access" prompt.
            // We deliberately do NOT cross-check the user's live server grants to suppress these:
            // approval writes the grant server-side immediately, so the grant always exists by the
            // time we'd check — yet this stale SPA session hasn't picked it up (that's the whole
            // reason this card exists). This placeholder/banner only renders when the current session
            // has no accessible game, so a live APPROVED request here is by definition not-yet-applied
            // and must show. Re-nagging after the user acts is prevented by the localStorage `acked`
            // set (written by onEnter), not by the server grant.
            const resp = await getMyGameAccessRequests({ accessToken: user.accessToken });
            // Endpoint serializes the list under `accessRequests` (GetUserGameAccessRequestsResponse).
            setRequests(resp?.accessRequests || []);
        } catch (err) {
            console.error('Error fetching my game access requests:', err);
        } finally {
            setLoading(false);
            checkingRef.current = false;
        }
    }, [user?.accessToken]);

    useEffect(() => {
        if (!user?.accessToken) return undefined;
        refresh();
        const onVisible = () => { if (!document.hidden) refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [user?.accessToken, refresh]);

    const newlyApproved = useMemo(
        () => requests.filter((r) => r.accessStatus === 'APPROVED' && !acked.has(requestKey(r))),
        [requests, acked],
    );

    const onEnter = useCallback(() => {
        setEntering(true);
        // Remember these approvals so the prompt doesn't reappear after the reload.
        const next = new Set(acked);
        newlyApproved.forEach((r) => next.add(requestKey(r)));
        writeAcked(next);
        setAcked(next);
        // Signal the post-reload bootstrap that access was JUST granted, so RequireGameAccess retries
        // a transient 0-accessible result (the new grant can lag the accessibility read by a beat)
        // instead of flashing the "No game access yet" placeholder before it resolves.
        try { sessionStorage.setItem('gp_access_just_refreshed', '1'); } catch (_) { /* ignore */ }
        // HARD reload to the home dashboard (no re-login — the token persists). A SPA navigate is not
        // enough: RequireGameAccess caches a per-org "game accessibility" snapshot in Redux and only
        // re-fetches it when the org / selected game changes, and the header's game switcher fetches
        // its list once on mount — so an in-place navigate keeps both stale and the user stays stuck on
        // "No game access yet". A full reload clears that cached state and re-bootstraps user +
        // permissions + accessibility from scratch, so the just-granted game is picked up.
        window.location.assign(LANDING_PATH);
    }, [acked, newlyApproved]);

    return { requests, loading, newlyApproved, entering, onEnter, refresh };
};
